import { useMutation } from '@apollo/client/react';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Row,
  Switch,
  Tooltip,
} from 'antd';
import { filter, keys } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import useLazyQueryWithCallbacks from '../../../../../../common/CommonUseLazyQuery';
import {
  ACCESS_TYPE,
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  DATE_ARITHMETIC_FUNCTIONS,
  DATE_FUNCTION_TYPES,
  FIELD_TYPES,
  MATHEMATICAL_FUNCTIONS_FOR_DETAIL,
  PROPERTY_TYPES,
  PROPERTY_TYPES_LABELS,
  REGEX,
  REGISTER_CALCULATION_TYPES,
  REGISTER_DETAILS_TYPES,
  TIME_FUNCTION_TYPES,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import {
  formValidatorRules,
  getFilteredFields,
} from '../../../../../../common/utils';
import CanPerform from '../../../../../../components/CanPerform';
import CommonSelect from '../../../../../../components/CommonSelect';
import HasAccess from '../../../../../../components/HasAccess';
import RouterPrompt from '../../../../../../components/RouterPrompt';
import {
  CREATE_REGISTER_DETAILS,
  UPDATE_REGISTER_DETAIL,
} from '../../../../graphql/Mutation';
import {
  GET_REGISTER,
  PROJECT_REGISTER_NOMENCLATURE_LIST,
} from '../../../../graphql/Queries';
import CalculationUi from './CalculationUI';
import CommonRegisterOptionUi from './CommonRegisterOptionUi';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

const AddEditPropertyModal = ({
  showModal,
  setShowModal,
  propertyDetails,
  setPropertyDetails,
  setTableColumns,
  tableColumns,
  isDisabled = false,
  selectedProperty,
  setSelectedProperty,
}) => {
  const {
    params: { registerId, projectId },
  } = useRouter();
  const { state, dispatch } = useContext(AppContext);
  const [form] = Form.useForm();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [numericFields, setNumericFields] = useState([]);
  const [numericTableFields, setNumericTableFields] = useState([]);
  const [dateFields, setDateFields] = useState([]);
  const [timeFields, setTimeFields] = useState([]);
  const [displayOption, setDisplayOption] = useState(false);
  const [isOptionType, setIsOptionType] = useState(false);
  const [hasNomenclature, setHasNomenclature] = useState(false);
  const [showTypeView, setShowTypeView] = useState(
    PROPERTY_TYPES_LABELS.NUMERIC,
  );
  const [activeFieldSegment, setActiveFieldSegment] = useState(
    REGISTER_DETAILS_TYPES.DETAIL,
  );
  const [formulaFieldValue, setFormulaFieldValue] = useState({
    [REGISTER_DETAILS_TYPES.DETAIL]: {
      [PROPERTY_TYPES_LABELS.NUMERIC]: '',
      [PROPERTY_TYPES_LABELS.DATE]: '',
      [PROPERTY_TYPES_LABELS.TIME]: '',
    },
    [REGISTER_DETAILS_TYPES.FORM_FIELD]: {
      [PROPERTY_TYPES_LABELS.NUMERIC]: '',
      [PROPERTY_TYPES_LABELS.DATE]: '',
      [PROPERTY_TYPES_LABELS.TIME]: '',
    },
  });
  const optionsInputRef = useRef(null);
  const shouldCloseModalRef = useRef(false);
  const hasInspectionAccess = HasAccess({ type: ACCESS_TYPE?.INSPECTION });

  const showRouterPrompt = (value) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: value,
    });
  };

  const [getProjectRegisterNomenclatureList] = useLazyQueryWithCallbacks(
    PROJECT_REGISTER_NOMENCLATURE_LIST,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const list = res?.projectRegisterNomenclatureList?.data || [];
        const isPublished = res?.projectRegisterNomenclatureList?.isPublished;
        setHasNomenclature(list?.length > 0 && isPublished === true);
      },
    },
  );

  const handleDropdownOpen = (open) => {
    if (open) {
      getProjectRegisterNomenclatureList({
        variables: { filter: { projectId } },
      });
    }
  };

  const [getRegister] = useLazyQueryWithCallbacks(GET_REGISTER, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted(response) {
      const registerDetails = response?.getRegister?.registerDetails;
      const registerFormFields = response?.getRegister?.registerFormFields;
      setPropertyDetails(registerDetails);
      setTableColumns(registerFormFields);
      if (shouldCloseModalRef.current) {
        setShowModal(false);
        shouldCloseModalRef.current = false;
      }
    },
  });

  const [createRegisterDetails] = useMutation(CREATE_REGISTER_DETAILS, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted() {
      shouldCloseModalRef.current = true;
      getRegister({ variables: { getRegisterId: registerId } });
    },
  });

  const [updateRegisterDetails] = useMutation(UPDATE_REGISTER_DETAIL, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted() {
      setSelectedProperty();
      shouldCloseModalRef.current = true;
      getRegister({ variables: { getRegisterId: registerId } });
    },
  });

  const handleTypeChange = (options) => {
    switch (options?.value) {
      case PROPERTY_TYPES.OPTIONS:
        setDisplayOption(true);
        setIsOptionType(true);
        break;

      case PROPERTY_TYPES.CHECKBOXES:
        setDisplayOption(true);
        setIsOptionType(true);
        break;

      case PROPERTY_TYPES.CALCULATION:
        setDisplayOption(true);
        setIsOptionType(false);
        getRegister({ variables: { getRegisterId: registerId } });
        break;

      default:
        setDisplayOption(false);
        setIsOptionType(false);
        break;
    }
  };

  useEffect(() => {
    if (selectedProperty) {
      const transformedValues = {
        ...selectedProperty,
        options:
          selectedProperty?.options?.map((option) => ({ option })) || null,
      };
      form.setFieldsValue(transformedValues);
      handleTypeChange({ value: selectedProperty?.type });

      // Set the activeFieldSegment based on calculationFieldType from API
      if (selectedProperty?.calculationFieldType) {
        setActiveFieldSegment(selectedProperty?.calculationFieldType);
      }

      // Convert UUID-based formula back to field names for editing
      if (selectedProperty?.type === PROPERTY_TYPES.CALCULATION) {
        const formattedFormula = selectedProperty?.options?.[0] || '';
        const replacedFormula = formattedFormula?.replace(
          REGEX.UUID,
          (match) => {
            const numericField = filter(
              numericFields,
              (record) => match === record?.id,
            )?.[0];
            if (numericField) {
              return `"${numericField?.name}"`;
            }

            const dateField = filter(
              dateFields,
              (record) => match === record?.id,
            )?.[0];
            if (dateField) {
              return `"${dateField?.name}"`;
            }

            const timeField = filter(
              timeFields,
              (record) => match === record?.id,
            )?.[0];
            if (timeField) {
              return `"${timeField?.name}"`;
            }

            const numericTableField = filter(
              numericTableFields,
              (record) => match === record?.id,
            )?.[0];
            if (numericTableField) {
              return `"${numericTableField?.name}"`;
            }

            return match;
          },
        );
        form.setFieldsValue({ formula: replacedFormula });

        // Determine calculation type based on formula content
        let calculationType = PROPERTY_TYPES_LABELS.NUMERIC;
        if (
          replacedFormula?.includes(DATE_FUNCTION_TYPES.DAYS_BETWEEN) ||
          replacedFormula?.includes(DATE_FUNCTION_TYPES.ADD_DAYS) ||
          replacedFormula?.includes(DATE_FUNCTION_TYPES.SUBTRACT_DAYS)
        ) {
          calculationType = PROPERTY_TYPES_LABELS.DATE;
        } else if (
          replacedFormula?.includes(TIME_FUNCTION_TYPES.TIME_SUBTRACTION)
        ) {
          calculationType = PROPERTY_TYPES_LABELS.TIME;
        }

        // Set the formula in the correct nested structure
        const fieldType =
          selectedProperty?.calculationFieldType ||
          REGISTER_DETAILS_TYPES.DETAIL;
        setFormulaFieldValue((prev) => ({
          ...prev,
          [fieldType]: {
            ...prev[fieldType],
            [calculationType]: replacedFormula,
          },
        }));

        // Set the appropriate calculation type view
        setShowTypeView(calculationType);
      }
    }
  }, [
    selectedProperty,
    numericFields,
    dateFields,
    timeFields,
    numericTableFields,
  ]);

  const handleSelect = (labeledValue, options) => {
    const existingOptions = form.getFieldValue('options');
    handleTypeChange(options);
    if (labeledValue === PROPERTY_TYPES.OPTIONS) {
      form.setFieldsValue({
        options: existingOptions?.length ? existingOptions : [{}, {}],
      });
    } else if (labeledValue === PROPERTY_TYPES.CHECKBOXES) {
      form.setFieldsValue({
        options: existingOptions?.length ? existingOptions : [{}, {}],
      });
    }
  };

  const handleCancel = () => {
    showRouterPrompt(false);
    setShowModal(false);
    setSelectedProperty();
  };

  const onPropertySubmitFinish = async () => {
    const formValues = form.getFieldsValue();

    if (
      (formValues?.type === PROPERTY_TYPES.OPTIONS ||
        formValues?.type === PROPERTY_TYPES.CHECKBOXES) &&
      formValues?.options
    ) {
      formValues.options = formValues.options
        .map((item) => item?.option)
        .filter(Boolean);
    }

    if (formValues?.type === FIELD_TYPES.CALCULATION) {
      const optionArr = [];
      if (registerId) {
        const formattedFormula = formValues?.formula?.replace(
          /"([^"]+)"/g,
          (match) => {
            const numericField = filter(
              numericFields,
              (record) => match === `"${record?.name}"`,
            )?.[0];
            if (numericField) {
              return numericField?.id;
            }
            const dateField = filter(
              dateFields,
              (record) => match === `"${record?.name}"`,
            )?.[0];
            if (dateField) {
              return dateField.id;
            }
            const timeField = filter(
              timeFields,
              (record) => match === `"${record?.name}"`,
            )?.[0];
            if (timeField) {
              return timeField.id;
            }
            const numericTableField = filter(
              numericTableFields,
              (record) => match === `"${record?.name}"`,
            )?.[0];
            if (numericTableField) {
              return numericTableField?.id;
            }
            return match;
          },
        );
        formValues.options = [formattedFormula];
        const formulaValue = formattedFormula
          ?.replace(/\\"/g, '')
          ?.replace(/"/g, '');
        optionArr?.push(formulaValue);
      } else {
        formValues.options = [formValues?.formula];
        const formulaValue = formValues?.formula
          ?.replace(/\\"/g, '')
          ?.replace(/"/g, '');
        optionArr?.push(formulaValue);
      }
      formValues.options = optionArr;

      const formula = formValues?.options?.[0] || '';
      if (formula?.includes(DATE_FUNCTION_TYPES.DAYS_BETWEEN)) {
        formValues.calculationResultType = REGISTER_CALCULATION_TYPES.NUMERIC;
      } else if (
        DATE_ARITHMETIC_FUNCTIONS.some((func) => formula?.includes(func))
      ) {
        formValues.calculationResultType = REGISTER_CALCULATION_TYPES.DATE;
      } else if (formula?.includes(TIME_FUNCTION_TYPES.TIME_SUBTRACTION)) {
        formValues.calculationResultType = REGISTER_CALCULATION_TYPES.NUMERIC;
      } else {
        formValues.calculationResultType = REGISTER_CALCULATION_TYPES.NUMERIC;
      }

      delete formValues?.formula;

      if (
        formValues?.options?.[0]?.includes(TIME_FUNCTION_TYPES.TIME_SUBTRACTION)
      ) {
        if (!formValues?.name?.includes('(hrs.)')) {
          formValues.name = `${formValues?.name} (hrs.)`;
        }
      }
      if (
        formValues?.options?.[0]?.includes(DATE_FUNCTION_TYPES.DAYS_BETWEEN)
      ) {
        if (!formValues?.name?.includes('(days)')) {
          formValues.name = `${formValues?.name} (days)`;
        }
      }
    }

    if (registerId) {
      if (!selectedProperty) {
        if (propertyDetails?.find((item) => item?.name === formValues?.name)) {
          message.destroy();
          message.error('Detail name already exists.');
        } else {
          const createData = {
            ...formValues,
            options: formValues?.options || null,
            registerId,
            sequence:
              propertyDetails?.[propertyDetails?.length - 1]?.sequence + 1 || 1,
          };

          if (formValues?.type === FIELD_TYPES.CALCULATION) {
            createData.calculationResultType =
              formValues?.calculationResultType ||
              REGISTER_CALCULATION_TYPES?.NUMERIC;
            createData.calculationFieldType = activeFieldSegment;
          }

          await createRegisterDetails({
            variables: {
              data: createData,
            },
          });
          showRouterPrompt(false);
        }
      } else if (
        propertyDetails?.find(
          (item) =>
            item?.name === formValues?.name &&
            item?.id !== selectedProperty?.id,
        )
      ) {
        message.destroy();
        message.error('Detail name already exists.');
      } else {
        const updateData = {
          ...formValues,
          options: formValues?.options || null,
          sequence: selectedProperty?.sequence,
        };

        // Only include calculation-specific fields if type is CALCULATION
        if (formValues?.type === FIELD_TYPES.CALCULATION) {
          updateData.calculationResultType =
            formValues?.calculationResultType ||
            REGISTER_CALCULATION_TYPES?.NUMERIC;
          updateData.calculationFieldType = activeFieldSegment;
        }

        await updateRegisterDetails({
          variables: {
            updateRegisterDetailId: selectedProperty?.id,
            data: updateData,
          },
        });
        showRouterPrompt(false);
      }
    }
  };
  const getTypeOptions = () => {
    return keys(PROPERTY_TYPES)?.map((type) => {
      const label = PROPERTY_TYPES_LABELS?.[type];
      const isNomenclature = type === PROPERTY_TYPES?.NOMENCLATURE;
      const isEQC = type === PROPERTY_TYPES?.EQC;
      const shouldDisableNomenclature = isNomenclature && !hasNomenclature;
      const shouldDisableEQC = isEQC && !hasInspectionAccess;
      const shouldDisable = shouldDisableNomenclature || shouldDisableEQC;
      if (shouldDisable) {
        let tooltipText = '';
        if (shouldDisableNomenclature) {
          tooltipText =
            'Set up nomenclature to activate this field option. else use text field';
        } else if (shouldDisableEQC) {
          tooltipText =
            'You do not have inspection access to activate this field option.';
        }
        return {
          key: label,
          value: type,
          disabled: true,
          label: <Tooltip title={tooltipText}>{label}</Tooltip>,
        };
      }
      return {
        key: label,
        value: type,
        label,
      };
    });
  };
  useEffect(() => {
    let filteredNumericFields = [];
    let filteredDateFields = [];
    let filteredTimeFields = [];

    filteredNumericFields = getFilteredFields(
      [PROPERTY_TYPES.NUMERIC],
      REGISTER_CALCULATION_TYPES.NUMERIC,
      propertyDetails,
      selectedProperty,
    );

    filteredDateFields = getFilteredFields(
      [PROPERTY_TYPES.DATE],
      REGISTER_CALCULATION_TYPES.DATE,
      propertyDetails,
      selectedProperty,
    );

    filteredTimeFields = getFilteredFields(
      [PROPERTY_TYPES.TIME],
      REGISTER_CALCULATION_TYPES.TIME,
      propertyDetails,
      selectedProperty,
    );

    setNumericFields(filteredNumericFields);
    setDateFields(filteredDateFields);
    setTimeFields(filteredTimeFields);
  }, [propertyDetails]);

  useEffect(() => {
    let filteredNumericFields = [];

    filteredNumericFields = getFilteredFields(
      [FIELD_TYPES.NUMERIC],
      REGISTER_CALCULATION_TYPES.NUMERIC,
      tableColumns,
    );

    setNumericTableFields(filteredNumericFields);
  }, [tableColumns]);

  return (
    <div>
      <RouterPrompt openPrompt={state?.showPrompt} />
      <Modal
        open={showModal}
        footer={false}
        centered
        className="stageitem-modal"
        maskClosable={false}
        closable={false}
        form={form}
      >
        <h2 className="mb-15">{selectedProperty ? 'Edit ' : 'Add '} Detail</h2>
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            type: 'TEXT',
            isRequired: true,
            options: [{}, {}],
          }}
          onFieldsChange={() => {
            showRouterPrompt(true);
          }}
          className="mt-16"
          onFinish={onPropertySubmitFinish}
        >
          <Form.Item
            rules={[
              required,
              characterWithoutWhiteSpace,
              { max: 250, message: 'Maximum 250 characters are allowed' },
            ]}
            name="name"
            label="Property Name"
          >
            <Input
              placeholder="Enter property name"
              disabled={isDisabled}
              allowClear
            />
          </Form.Item>
          <Row gutter={30}>
            <Col span={isDesktopViewport ? 18 : 24}>
              <Form.Item name="type" label="Type">
                <CommonSelect
                  className="mr-3"
                  placeholder="Select Type"
                  onSelect={handleSelect}
                  options={getTypeOptions()}
                  disabled={isDisabled}
                  onOpenChange={handleDropdownOpen}
                />
              </Form.Item>
            </Col>
            <Col span={isDesktopViewport ? 6 : 24}>
              <Form.Item
                name="isRequired"
                label="Required"
                valuePropName="checked"
              >
                <Switch defaultChecked disabled={isDisabled} />
              </Form.Item>
            </Col>
          </Row>
          {displayOption && (
            <>
              <Divider dashed />
              {isOptionType ? (
                <>
                  <h3>Options</h3>
                  <CommonRegisterOptionUi
                    isDisabled={isDisabled}
                    form={form}
                    optionsInputRef={optionsInputRef}
                  />
                </>
              ) : (
                <h3>Calculation</h3>
              )}
              <div className="mt-10">
                {!isOptionType && (
                  <CalculationUi
                    isDisabled={isDisabled}
                    numericFields={numericFields}
                    dateFields={dateFields}
                    timeFields={timeFields}
                    numericTableFields={numericTableFields}
                    form={form}
                    showTypeView={showTypeView}
                    setShowTypeView={setShowTypeView}
                    formulaFieldValue={formulaFieldValue}
                    setFormulaFieldValue={setFormulaFieldValue}
                    mathematicalFunctions={MATHEMATICAL_FUNCTIONS_FOR_DETAIL}
                    activeFieldSegment={activeFieldSegment}
                    setActiveFieldSegment={setActiveFieldSegment}
                  />
                )}
              </div>
            </>
          )}
          <div className="form-buttons">
            <Button
              shape="round"
              className="cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <CanPerform
              action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
              type={ALLOWED_ACTION_TYPE.BOTH}
            >
              {!isDisabled && (
                <Button
                  shape="round"
                  type="primary"
                  className="save-button"
                  htmlType="submit"
                >
                  Save
                </Button>
              )}
            </CanPerform>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddEditPropertyModal;
